import { carePhaseDefs } from '@shared';
import { DetailList } from '../components/figures';
import { StepFrame } from '../components/step-frame';
import { useConsumerClient } from '../hooks/use-consumer-client';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { CARE_ENVIRONMENT_LABELS } from '../lib/copy';
import { approxMoney } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

export function BreakdownStep({ clientId }: ConsumerStepProps) {
  const { finish } = useConsumerFlow(clientId);
  const { scenario } = useConsumerClient(clientId);
  if (!scenario) return null;

  return (
    <StepFrame
      step="breakdown"
      title="What’s included in each stage of care?"
      subtitle={`Costs rise ${scenario.inflationPercent.toFixed(1)}% a year until care starts at ${scenario.careStartAge}. Insurance premiums are separate.`}
      onBack={() => finish('review')}
      help={<p>Home care is priced by the hour for the help you need after family hours. Facilities are priced by the month for your area.</p>}
      primary={{ label: 'Back to my plan', onClick: () => finish('review') }}
    >
      {scenario.phases.map(phase => (
        <div key={phase.carePhase} className="flex flex-col gap-2">
          <h2 className="font-semibold text-gray-950">{carePhaseDefs[phase.carePhase].label}</h2>
          <DetailList
            rows={[
              { label: 'Where', value: CARE_ENVIRONMENT_LABELS[phase.careEnvironment] },
              { label: 'How long', value: `${Math.round(phase.months)} months` },
              ...(phase.inHome
                ? [{ label: 'Family time', value: `${phase.familyHoursPerWeek.toFixed(1)} hours a week` }]
                : []),
              { label: 'Projected cost for this stage', value: approxMoney(phase.cost) },
            ]}
          />
        </div>
      ))}
      <DetailList rows={[{ label: 'All stages together', value: approxMoney(scenario.totalCost) }]} />
    </StepFrame>
  );
}
